import { Fragment, useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Reports.module.css';
import { Tooltip, Typography, Zoom } from '@material-ui/core';
import LoadIndicator from 'components/common/LoadIndicator/LoadIndicator';
import { useGetQueryString } from 'hooks/useGetQueryString';
import { useSelector } from 'react-redux';
import QueryBuilder from 'components/common/QueryBuilder/QueryBuilder';
import { useReportingAPI } from '../hooks/useReportingAPI';
import { getApi } from 'api/axios';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid } from '@mui/x-data-grid';

const Reports = () => {
  const navigate = useNavigate();
  const shopId = useSelector((state) => state.nonPersistedReducers?.shopSliceReducer?.shopId);
  const { search } = useLocation();

  const routeChange = (id) => {
    let path = `/shops/${shopId}/orders/${id}`;
    navigate(path);
  };

  const { getQueryString, getDateString } = useGetQueryString();

  // Last full week: Monday 00:00:00 UTC → next Monday 00:00:01 UTC (only used when no from/to chosen)
  const getLastFullWeekRange = () => {
    const now = new Date();
    const daysBackToThisMonday = (now.getUTCDay() + 6) % 7;
    const thisMonday = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - daysBackToThisMonday,
        0,
        0,
        0,
        0
      )
    );
    const lastMonday = new Date(thisMonday.getTime() - 7 * 24 * 60 * 60 * 1000);
    const nextMonday0001 = new Date(thisMonday.getTime() + 1000);
    return { from: lastMonday, to: nextMonday0001 };
  };

  const defaultReportRange = getLastFullWeekRange();
  const queryParams = [
    {
      name: 'report',
      value: `fromDateTime=${getDateString(defaultReportRange.from)}&toDateTime=${getDateString(defaultReportRange.to)}`
    }
  ];

  const {
    fetchAllReports,
    fetchAllPaymentsAggregated,
    fetchAllPrinted,
    allReports,
    allPaymentsAggregated,
    allReportsLength,
    allReportsloading,
    allPaymentsAggregateloading
  } = useReportingAPI();

  const refresh = () => {
    if (search) {
      // fetchAllReports({ queryString: search });
      fetchAllPaymentsAggregated({ queryString: search });
    } else {
      // fetchAllReports({ queryString: getQueryString(queryParams) });
      // fetchAllPaymentsAggregated({ queryString: getQueryString(queryParams) });
    }
  };

  const linkRef = useRef();

  const handleGetCSV = async (type) => {
    try {
      const pathMap = {
        aggregated: 'payments/report/aggregated',
        'aggregated-legacy': 'reports/aggregated',
        'aggregated-printing': 'reports/aggregated/printing-price',
        default: 'reports'
      };
      const path = pathMap[type] ?? pathMap.default;
      const queryString = search ? search : getQueryString(queryParams);

      const res = await (
        await getApi({ baseUrl: window._env_.REACT_APP_QDAK_ORDER_URL })
      ).get(`/admin/${path}${queryString}`, {
        headers: {
          Accept: 'text/csv',
          'Content-Type': 'text/csv'
        },
        responseType: 'blob'
      });

      const href = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv; charset=UTF-8' }));

      const a = linkRef.current;
      const filenameMap = {
        aggregated: 'Aggregalt_kimutatas',
        'aggregated-legacy': 'Aggregalt_kimutatas_regi',
        'aggregated-printing': 'Nyomtatasi_arak_aggregalt_kimutatasa',
        default: 'Kimutatas'
      };
      a.download = `${filenameMap[type] ?? filenameMap.default}_lista ${new Date().toLocaleDateString('hu-HU')} .csv`;
      a.href = href;
      a.click();
      a.href = '';
    } catch (e) {
      console.log(e);
    }
  };

  const resetQueryString = () => {
    navigate(`/superadmin/dashboard${getQueryString(queryParams)}`);
  };

  useEffect(() => {
    console.log('search', search);
    refresh();
  }, [search]);

  useEffect(() => {
    //fetchAllReports({ queryString: getQueryString(queryParams) });
    // fetchAllPaymentsAggregated({ queryString: getQueryString(queryParams) });
    // fetchAllPrinted({ queryString: getQueryString(queryParams) });
  }, []);

  const columns = [
    { field: 'shopId', headerName: 'Bolt azonosító', minWidth: 130, sortable: false },
    { field: 'shopOwner', headerName: 'Bolt tulajdonos', minWidth: 150, sortable: false },
    { field: 'vatNumber', headerName: 'Vat szám', minWidth: 120, sortable: false },
    { field: 'bankAccountNumber', headerName: 'Számlaszám', minWidth: 250, sortable: false },
    { field: 'billingAddress', headerName: 'Számlázási cím', minWidth: 350, sortable: false },
    { field: 'totalPrice', headerName: 'Teljes ár', type: 'number', minWidth: 100, sortable: false },
    { field: 'commission', headerName: 'Jutalék', type: 'number', minWidth: 100, sortable: false },
    { field: 'commissionNet', headerName: 'Jutalék nettó', type: 'number', minWidth: 100, sortable: false },
    { field: 'printingPrice', headerName: 'Nyomtatási költség', type: 'number', minWidth: 160, sortable: false },
    { field: 'clientProfitGross', headerName: 'Felhasználó profit', type: 'number', minWidth: 150, sortable: false },
    { field: 'email', headerName: 'Email', minWidth: 250, sortable: false }
  ];

  return (
    <Fragment>
      <a ref={linkRef} />
      <Typography
        component='h2'
        variant='h6'
        color='primary'
        gutterBottom
        style={{ fontFamily: 'var(--font-main), Sans-serif, Arial' }}>
        Kimutatások
      </Typography>

      <Zoom in={true} timeout={700}>
        <QueryBuilder
          itemsLength={allReportsLength}
          siteType={'reporting'}
          reset={resetQueryString}
          refresh={refresh}
          OriqueryParams={queryParams}>
          <>
            <div className={styles.downloadBar}>
              <Tooltip
                title={
                  'Kimutatás összesítő lista letöltése a szűrő feltételek szerint. Ha nincs megadva időszak, alapértelmezett: előző teljes hét (hétfő 00:00:00 – következő hétfő 00:00:01).'
                }>
                <Button
                  variant='outlined'
                  disabled={allReportsLength < 1}
                  style={{
                    backgroundColor: 'var(--primary-background-content)',
                    color: 'var(--primary-main)',
                    margin: 0,
                    border: '2px solid var(--primary-background-primary)'
                  }}
                  onClick={() => handleGetCSV()}>
                  <FileDownloadIcon />
                  Kimutatás letöltés
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  'Kimutatás összesítő lista letöltése (új API). Ha nincs megadva időszak, alapértelmezett: előző teljes hét (hétfő 00:00:00 – következő hétfő 00:00:01).'
                }>
                <Button
                  variant='outlined'
                  disabled={allReports?.length < 1}
                  style={{
                    backgroundColor: 'var(--primary-background-content)',
                    color: 'var(--primary-main)',
                    margin: 0,
                    border: '2px solid var(--primary-background-primary)'
                  }}
                  onClick={() => handleGetCSV('aggregated')}>
                  <FileDownloadIcon />
                  Aggregált kimutatás letöltés
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  'Kimutatás összesítő lista letöltése (régi API). Ha nincs megadva időszak, alapértelmezett: előző teljes hét (hétfő 00:00:00 – következő hétfő 00:00:01).'
                }>
                <Button
                  variant='outlined'
                  disabled={allReports?.length < 1}
                  style={{
                    backgroundColor: 'var(--primary-background-content)',
                    color: 'var(--primary-main)',
                    margin: 0,
                    border: '2px solid var(--primary-background-primary)'
                  }}
                  onClick={() => handleGetCSV('aggregated-legacy')}>
                  <FileDownloadIcon />
                  Aggregált kimutatás letöltés (régi)
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  'Nyomtatási árak aggregált kimutatás letöltése. Ha nincs megadva időszak, alapértelmezett: előző teljes hét (hétfő 00:00:00 – következő hétfő 00:00:01).'
                }>
                <Button
                  variant='outlined'
                  disabled={allReports?.length < 1}
                  style={{
                    backgroundColor: 'var(--primary-background-content)',
                    color: 'var(--primary-main)',
                    margin: 0,
                    border: '2px solid var(--primary-background-primary)'
                  }}
                  onClick={() => handleGetCSV('aggregated-printing')}>
                  <FileDownloadIcon />
                  Nyomtatási árak aggregált kimutatás letöltés
                </Button>
              </Tooltip>
            </div>

            <DataGrid
              getRowId={(row) => row.shopId + row.shopId * Math.random()}
              disableColumnMenu
              disableSelectionOnClick
              disableColumnSelector
              disableColumnFilter
              columns={columns}
              rows={allPaymentsAggregated ?? []}
              pageSizeOptions={[5, 10, 25, 50, 100]}
            />
          </>
        </QueryBuilder>
      </Zoom>

      <div className={styles.seeMore}>
        <Button variant='contained' color='primary' onClick={() => navigate(`/superadmin/dashboard`)}>
          Több jelentés megtekintése
        </Button>
      </div>
    </Fragment>
  );
};

export default Reports;
